import React from 'react';
import { PageSettings } from '../../../core/ast/document.types';
import { DOCUMENT_DEFAULTS } from '../../../config/document.config';
import { Sliders, Palette } from 'lucide-react';

interface TypographyLayoutControlsProps {
  pageSettings: PageSettings;
  accentColor?: string;
  borderStyle?: string;
  onUpdatePageSettings: (updates: Partial<PageSettings>) => void;
  onUpdateAccentColor: (color: string) => void;
  onUpdateBorderStyle: (borderStyle: any) => void;
}

const ACCENT_COLOR_PRESETS = [
  { label: 'Navy Blue', hex: '#1e3a8a' },
  { label: 'Academic Blue', hex: '#1d4ed8' },
  { label: 'Crimson', hex: '#991b1b' },
  { label: 'Emerald', hex: '#065f46' },
  { label: 'Slate', hex: '#334155' },
  { label: 'Gold Accent', hex: '#b45309' },
];

export const TypographyLayoutControls: React.FC<TypographyLayoutControlsProps> = ({
  pageSettings,
  accentColor = '#1e3a8a',
  borderStyle = 'single',
  onUpdatePageSettings,
  onUpdateAccentColor,
  onUpdateBorderStyle,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-4)', backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', borderBottom: '1px solid var(--border-subtle)', paddingBottom: 'var(--space-2)' }}>
        <Sliders size={16} color="var(--primary-600)" />
        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Typography & Layout Styling
        </span>
      </div>

      {/* Font Family & Base Size */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 'var(--space-3)' }}>
        <div className="form-group">
          <label className="form-label">Font Family</label>
          <select
            className="form-select text-xs"
            value={pageSettings.defaultFontFamily}
            onChange={(e) => onUpdatePageSettings({ defaultFontFamily: e.target.value })}
          >
            {DOCUMENT_DEFAULTS.supportedFonts.map((f) => (
              <option key={f.id} value={f.id}>
                {f.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Paper Format</label>
          <select
            className="form-select text-xs"
            value={pageSettings.paperSize}
            onChange={(e) => onUpdatePageSettings({ paperSize: e.target.value as any })}
          >
            {DOCUMENT_DEFAULTS.supportedPaperSizes.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Accent Color Palette */}
      <div className="form-group">
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Palette size={12} />
          <span>Institutional Accent Color</span>
        </label>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          {ACCENT_COLOR_PRESETS.map((color) => (
            <button
              key={color.hex}
              type="button"
              onClick={() => onUpdateAccentColor(color.hex)}
              style={{
                width: 24,
                height: 24,
                borderRadius: 'var(--radius-full)',
                backgroundColor: color.hex,
                border: accentColor === color.hex ? '2px solid #ffffff' : '1px solid rgba(0,0,0,0.1)',
                boxShadow: accentColor === color.hex ? '0 0 0 2px var(--primary-600)' : 'none',
                cursor: 'pointer',
              }}
              title={color.label}
            />
          ))}
          <input
            type="color"
            value={accentColor}
            onChange={(e) => onUpdateAccentColor(e.target.value)}
            style={{ width: 26, height: 26, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }}
            title="Custom Hex Color"
          />
        </div>
      </div>

      {/* Border Style */}
      <div className="form-group">
        <label className="form-label">Cover Frame & Border Style</label>
        <select
          className="form-select text-xs"
          value={borderStyle}
          onChange={(e) => onUpdateBorderStyle(e.target.value)}
        >
          <option value="single">Single Classic Border</option>
          <option value="double">Formal Double Border</option>
          <option value="corner_accents">Modern Corner Accents</option>
          <option value="academic_box">Academic Submission Box</option>
          <option value="none">No Outer Border</option>
        </select>
      </div>
    </div>
  );
};
